import SubstanceListReferenceGraph from '@core/domain/substance/SubstanceListReferenceGraph'
import { SubstanceState } from '@/libs/product'
import { getSubstanceListId } from './scip-applicability'
import {
    complexRequirementChecks,
    requirementStatus,
} from './requirement-checks'
import { complianceRequirementCheckerRegistry } from './requirement-checker-registry'

const normalizeId = value => (
    value === null || typeof value === 'undefined' ? null : String(value)
)

const VALID_COMPONENT_STATES = [
    SubstanceState.ALL_DEFINED_INCLUDES_SUBSTANCES,
    SubstanceState.ALL_DEFINED_DOES_NOT_INCLUDE_SUBSTANCES,
]

const isEmptyValue = value => {
    if (value === null || typeof value === 'undefined' || value === '') {
        return true
    }
    return Array.isArray(value) && value.length === 0
}

const containerBelongsToList = (container, listId) => (
    Array.isArray(container && container.lists)
    && container.lists.some(list => normalizeId(
        list && typeof list === 'object' ? list.id : list,
    ) === normalizeId(listId))
)

const propertyValue = (container, property, listId) => {
    const properties = container && !Array.isArray(container.properties)
        ? container.properties || {}
        : {}
    const value = properties[property.type]

    if (!property.listSpecific) {
        return value
    }

    if (!Array.isArray(value)) {
        return null
    }

    const item = value.find(entry => normalizeId(entry && entry.list && entry.list.id) === normalizeId(listId))
    return item ? item.value : null
}

const listPropertyDefinitions = (list, propertyConfig) => {
    const listMap = propertyConfig && propertyConfig.map && propertyConfig.map.list
        ? propertyConfig.map.list
        : {}
    const properties = propertyConfig && propertyConfig.properties
        ? propertyConfig.properties
        : {}
    const propertyNames = listMap[list.id] || listMap[String(list.id)] || []

    return propertyNames
        .filter(type => properties[type])
        .map(type => ({ ...properties[type], type }))
}

const missingProperties = (container, list, propertyConfig) => {
    const definitions = listPropertyDefinitions(list, propertyConfig)
    return definitions
        .filter(property => property.properties && property.properties.required === true)
        .filter(property => isEmptyValue(propertyValue(container, property, list.id)))
        .map(property => property.type)
}

const configuredPlacementPropertyValue = (placement, type, fallbackList, propertyConfig) => {
    const list = placement.list || fallbackList
    const definition = listPropertyDefinitions(list, propertyConfig)
        .find(property => property.type === type)

    if (definition) {
        return propertyValue(placement.container, definition, list.id)
    }

    return (placement.container.properties || {})[type]
}

const withPlacementCompliance = (placements, fallbackList, propertyConfig) => placements
    .map(placement => ({
        ...placement,
        compliance: complianceRequirementCheckerRegistry.evaluatePlacement({
            list: placement.list || fallbackList,
            placement,
            missingProperties: missingProperties(
                placement.container,
                placement.list || fallbackList,
                propertyConfig,
            ),
            propertyValue: (currentPlacement, type) => configuredPlacementPropertyValue(
                currentPlacement,
                type,
                fallbackList,
                propertyConfig,
            ),
        }),
    }))

const resolveListForLegislation = (legislation, substanceLists) => {
    const directId = getSubstanceListId(legislation)
    if (directId !== null) {
        return substanceLists.find(list => normalizeId(list.id) === normalizeId(directId)) || null
    }

    return substanceLists.find(list => list.hasIntersectionWithLegislations([
        legislation.id,
        legislation.key,
    ])) || null
}

const componentPath = component => {
    const parents = Array.isArray(component.parents) ? component.parents : []
    const titles = parents
        .map(parent => parent && (parent.title || (parent.component && parent.component.title)))
        .filter(Boolean)
    const currentTitle = component.component && component.component.title

    if (currentTitle && titles[titles.length - 1] !== currentTitle) {
        titles.push(currentTitle)
    }

    return titles.join(' / ')
}

const simpleProductPlacements = context => (
    (context.includedSubstances || []).map(container => ({
        container,
        component: null,
        componentPath: '',
    }))
)

const complexProductPlacements = context => (
    (context.componentConcernSubstances || []).reduce((placements, component) => (
        placements.concat((component.substances || []).map(container => ({
            container,
            component,
            componentPath: componentPath(component),
        })))
    ), [])
)

const questionStateForList = (product, listId) => {
    const states = product && Array.isArray(product.substanceListQuestionStates)
        ? product.substanceListQuestionStates
        : []
    const current = states.find(state => normalizeId(state.list) === normalizeId(listId))
    return current ? current.state : null
}

const substanceListProductContext = (product, resolvedContext = {}) => ({
    regulatedProductStatements: (product && product.regulatedProductStatements) || [],
    materialCategoryIds: (product && product.materialCategoryIds) || [],
    mixtureCategoryIds: (product && product.mixtureCategoryIds) || [],
    materialCategoryRelations: resolvedContext.materialCategoryRelations
        || (product && product.materialCategoryRelations)
        || {},
    mixtureCategoryRelations: resolvedContext.mixtureCategoryRelations
        || (product && product.mixtureCategoryRelations)
        || {},
})

const listsForRequirement = (graph, rootList, productContext) => {
    const referencedIds = graph.getTransitiveReferencedListIds(rootList, productContext)

    return [rootList].concat(referencedIds.map(id => graph.getListById(id)).filter(Boolean))
}

const branchListIds = (graph, list, productContext) => [list.id]
    .concat(graph.getTransitiveReferencedListIds(list, productContext))

const questionPathLabels = (pathsMap, list) => (pathsMap[list.id] || [])
    .map(path => path.map(parent => parent.title).filter(Boolean).join(' / '))
    .filter(Boolean)

const buildRequirementQuestions = ({
    graph,
    rootList,
    lists,
    pathsMap,
    placements,
    product,
    productContext,
}) => lists
    .filter(list => list.hasQuestion())
    .map(list => {
        const listIds = branchListIds(graph, list, productContext)
        const hasPlacement = placements.some(placement => listIds.some(listId => (
            containerBelongsToList(placement.container, listId)
        )))
        const storedState = questionStateForList(product, list.id)

        return {
            id: normalizeId(list.id),
            list,
            state: storedState === null && hasPlacement ? true : storedState,
            inferredState: storedState === null && hasPlacement,
            pathLabels: list.id === rootList.id ? [] : questionPathLabels(pathsMap, list),
            branchListIds: listIds,
        }
    })

const resolveSelectorListIds = ({
    graph,
    rootList,
    questions,
    productContext,
}) => {
    if (questions.length === 0) {
        return branchListIds(graph, rootList, productContext)
    }

    const states = questions.reduce((map, question) => ({
        ...map,
        [normalizeId(question.list.id)]: question.state,
    }), {})
    const selectedIds = new Set()
    const visit = (list, visitedIds) => {
        const id = normalizeId(list.id)
        if (visitedIds.includes(id)) {
            return
        }

        if (list.hasQuestion() && states[id] !== true) {
            return
        }

        selectedIds.add(id)

        graph.getVisibleReferences(list, productContext).forEach(reference => {
            const child = graph.getListById(reference.listId)
            if (child && graph.isListVisible(child, productContext)) {
                visit(child, visitedIds.concat([id]))
            }
        })
    }

    questions
        .filter(question => question.state === true)
        .forEach(question => visit(question.list, []))
    return Array.from(selectedIds)
}

const isComplexProduct = context => (
    context.request && (
        context.request.isComplexProduct === true
        || (context.request.product && context.request.product.isComplex === true)
    )
)

const complexDeclarationComplete = context => (
    (context.componentConcernSubstances || []).length > 0
    && context.componentConcernSubstances.every(component => VALID_COMPONENT_STATES.includes(component.state))
)

const componentEntryKey = component => String(
    component && component.component && component.component.id,
)

const hierarchyPositions = hierarchy => {
    const positions = {}
    let order = 0
    const visit = (node, depth) => {
        if (!node || typeof node !== 'object') {
            return
        }
        const key = normalizeId(node.id || (node.component && node.component.id))
        const currentOrder = order
        order += 1
        if (
            key !== null
            && (!positions[key] || depth < positions[key].depth)
        ) {
            positions[key] = { depth, order: currentOrder }
        }
        const children = Array.isArray(node.children) ? node.children : []
        children.forEach(child => visit(child, depth + 1))
    }

    visit(hierarchy, 0)
    return positions
}

const groupComplexComponents = (components, hierarchy) => {
    const groups = {}
    components.forEach((component, sourceOrder) => {
        const key = componentEntryKey(component)
        if (!groups[key]) {
            groups[key] = {
                key,
                component,
                componentPaths: [],
                fallbackDepth: Number.POSITIVE_INFINITY,
                sourceOrder,
            }
        }
        const depth = Array.isArray(component.parents)
            ? component.parents.length
            : Number.POSITIVE_INFINITY
        groups[key].fallbackDepth = Math.min(groups[key].fallbackDepth, depth)
        const path = componentPath(component)
        if (path && !groups[key].componentPaths.includes(path)) {
            groups[key].componentPaths.push(path)
        }
    })
    const positions = hierarchyPositions(hierarchy)
    return Object.values(groups).sort((left, right) => {
        const leftPosition = positions[left.key] || {}
        const rightPosition = positions[right.key] || {}
        const depthDifference = (leftPosition.depth ?? left.fallbackDepth)
            - (rightPosition.depth ?? right.fallbackDepth)
        if (depthDifference !== 0) {
            return depthDifference
        }

        const orderDifference = (leftPosition.order ?? left.sourceOrder)
            - (rightPosition.order ?? right.sourceOrder)
        if (orderDifference !== 0) {
            return orderDifference
        }

        return (left.componentPaths[0] || '').localeCompare(right.componentPaths[0] || '')
    })
}

const uniqueComponentPlacements = placements => {
    const unique = {}
    placements.forEach(placement => {
        const substanceId = placement.container
            && placement.container.substance
            && placement.container.substance.id
        const listId = placement.list && placement.list.id
        const key = `${substanceId}-${listId}`
        if (!unique[key]) {
            unique[key] = placement
        }
    })
    return Object.values(unique)
}

const buildComplianceRequirements = (context, substanceLists, propertyConfig) => {
    const requirementsByList = {}
    const legislations = (context.request && context.request.legislations) || []
    const graph = new SubstanceListReferenceGraph(substanceLists)
    const productContext = substanceListProductContext(
        context.product,
        (context.complianceInformation && context.complianceInformation.visibilityContext) || {},
    )

    legislations.forEach(legislation => {
        const list = resolveListForLegislation(legislation, substanceLists)
        if (!list) {
            return
        }

        const key = normalizeId(list.id)
        if (!requirementsByList[key]) {
            requirementsByList[key] = {
                id: key,
                list,
                legislations: [],
            }
        }
        requirementsByList[key].legislations.push(legislation)
    })

    const complex = isComplexProduct(context)
    const placements = complex
        ? complexProductPlacements(context)
        : simpleProductPlacements(context)
    const complexComplete = complexDeclarationComplete(context)

    return Object.values(requirementsByList).map(requirement => {
        const requirementLists = listsForRequirement(graph, requirement.list, productContext)
        const requirementListIds = requirementLists.map(list => list.id)
        const listPlacements = placements
            .filter(placement => requirementListIds.some(listId => (
                containerBelongsToList(placement.container, listId)
            )))
            .map(placement => ({
                ...placement,
                list: requirementLists.find(list => (
                    containerBelongsToList(placement.container, list.id)
                )) || requirement.list,
            }))
        const pathsMap = graph.buildReferencedToParentPathsMap(
            [requirement.list],
            requirementLists,
            productContext,
        )
        const questions = complex ? [] : buildRequirementQuestions({
            graph,
            rootList: requirement.list,
            lists: requirementLists,
            pathsMap,
            placements: listPlacements,
            product: context.product,
            productContext,
        })
        const selectorListIds = complex ? requirementListIds : resolveSelectorListIds({
            graph,
            rootList: requirement.list,
            questions,
            productContext,
        })
        const rootStoredQuestionState = questionStateForList(context.product, requirement.list.id)
        const rootQuestionState = rootStoredQuestionState === null && listPlacements.length > 0
            ? true
            : rootStoredQuestionState
        const inferredQuestions = questions.filter(question => question.inferredState)
        const activePlacements = questions.length === 0
            ? listPlacements
            : listPlacements.filter(placement => selectorListIds.some(listId => (
                containerBelongsToList(placement.container, listId)
            )))
        const missing = activePlacements.reduce((result, placement) => (
            result.concat(missingProperties(
                placement.container,
                placement.list,
                propertyConfig,
            ))
        ), [])
        const componentEntries = complex
            ? groupComplexComponents(
                context.componentConcernSubstances || [],
                context.productHierarchy,
            ).map(group => {
                const { key, component, componentPaths } = group
                const componentPlacements = uniqueComponentPlacements(
                    listPlacements.filter(placement => (
                        componentEntryKey(placement.component) === key
                    )),
                )
                const componentProduct = component.component || {}
                const componentProductContext = substanceListProductContext(
                    componentProduct,
                    (context.complianceInformation && context.complianceInformation.visibilityContext) || {},
                )
                const componentQuestions = buildRequirementQuestions({
                    graph,
                    rootList: requirement.list,
                    lists: requirementLists,
                    pathsMap,
                    placements: componentPlacements,
                    product: componentProduct,
                    productContext: componentProductContext,
                })
                const componentRootQuestion = componentQuestions.find(question => (
                    normalizeId(question.list.id) === normalizeId(requirement.list.id)
                ))
                const componentSelectorListIds = resolveSelectorListIds({
                    graph,
                    rootList: requirement.list,
                    questions: componentQuestions,
                    productContext: componentProductContext,
                })
                const componentActivePlacements = componentQuestions.length === 0
                    ? componentPlacements
                    : componentPlacements.filter(placement => componentSelectorListIds.some(listId => (
                        containerBelongsToList(placement.container, listId)
                    )))
                const componentMissing = componentActivePlacements.reduce((result, placement) => (
                    result.concat(missingProperties(
                        placement.container,
                        placement.list,
                        propertyConfig,
                    ))
                ), [])
                const componentEvaluation = complianceRequirementCheckerRegistry.evaluateList({
                    list: requirement.list,
                    legislations: requirement.legislations,
                    questions: componentQuestions,
                    placements: componentActivePlacements,
                    missingProperties: componentMissing,
                    productContext: componentProductContext,
                    propertyValue: (placement, type) => {
                        const definition = listPropertyDefinitions(
                            placement.list || requirement.list,
                            propertyConfig,
                        ).find(property => property.type === type)

                        return definition
                            ? propertyValue(
                                placement.container,
                                definition,
                                (placement.list || requirement.list).id,
                            )
                            : (placement.container.properties || {})[type]
                    },
                })

                return {
                    key,
                    component,
                    product: componentProduct,
                    title: componentProduct.title || `Component ${key}`,
                    componentPath: componentPaths[0] || '',
                    componentPaths,
                    questions: componentQuestions,
                    questionState: componentRootQuestion ? componentRootQuestion.state : null,
                    selectorListIds: componentSelectorListIds,
                    placements: withPlacementCompliance(
                        componentActivePlacements,
                        requirement.list,
                        propertyConfig,
                    ),
                    missingProperties: Array.from(new Set(componentEvaluation.missingProperties || [])),
                    checks: componentEvaluation.checks,
                    facts: componentEvaluation.facts,
                    safetyInstructions: component.safetyInstructions,
                    status: requirementStatus(componentEvaluation.checks),
                }
            })
            : []
        const evaluation = complex
            ? {
                checks: componentEntries.length > 0
                    ? complexRequirementChecks(
                        componentEntries.every(entry => entry.status === 'complete'),
                    )
                    : complexRequirementChecks(complexComplete),
                facts: [],
            }
            : complianceRequirementCheckerRegistry.evaluateList({
                list: requirement.list,
                legislations: requirement.legislations,
                questions,
                placements: activePlacements,
                missingProperties: missing,
                productContext,
                propertyValue: (placement, type) => {
                    const definition = listPropertyDefinitions(
                        placement.list || requirement.list,
                        propertyConfig,
                    ).find(property => property.type === type)

                    if (definition) {
                        return propertyValue(
                            placement.container,
                            definition,
                            (placement.list || requirement.list).id,
                        )
                    }

                    const properties = placement.container.properties || {}
                    return properties[type]
                },
            })

        return {
            ...requirement,
            lists: requirementLists,
            questions,
            questionState: rootQuestionState,
            inferredQuestionState: !complex
                && rootStoredQuestionState === null
                && listPlacements.length > 0,
            inferredQuestions,
            selectorListIds,
            hasReferencedLists: requirementLists.length > 1,
            placements: withPlacementCompliance(
                activePlacements,
                requirement.list,
                propertyConfig,
            ),
            missingProperties: Array.from(new Set(evaluation.missingProperties || [])),
            checks: evaluation.checks,
            facts: evaluation.facts,
            componentEntries,
            status: requirementStatus(evaluation.checks),
        }
    })
}

const uniqueSubstanceCount = requirements => {
    const ids = new Set()
    requirements.forEach(requirement => {
        requirement.placements.forEach(({ container }) => {
            if (container && container.substance && container.substance.id !== undefined) {
                ids.add(String(container.substance.id))
            }
        })
    })
    return ids.size
}

export {
    buildComplianceRequirements,
    isComplexProduct,
    listPropertyDefinitions,
    propertyValue,
    uniqueSubstanceCount,
}
